import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/AccountList.css';
import AdminHeader from '../../components/headers/AdminHeader';
import iconTitle from '../../assets/img/icon-square.svg';
import { listUsers, updateUser } from '../../services/api/UserService';
import { ConfirmationPopup } from '../../components/popups/AccountListDeletePop';
import { showSuccessDialog } from '../../providers/ErrorDialogProvider';

const AccountList = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // 削除確認用のポップアップ状態
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [confirmationPopupData, setConfirmationPopupData] = useState(null);

  useEffect(() => {
    // ローカルストレージから現在のユーザー情報を取得
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        setCurrentUser(parsedUserInfo);
      } catch (error) {
        console.error('ユーザー情報の解析に失敗しました:', error);
      }
    }

    // APIからユーザーリストを取得
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const response = await listUsers({
          skip: 0,
          limit: 100,
          is_admin: true,
          is_active: true
        });
        setAccounts(response);
        setLoading(false);
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  const handleEdit = (id) => {
    navigate(`/admin/account/edit/${id}`);
  };

  // 削除ボタンのクリックハンドラー
  const handleDelete = (id) => {
    // 対象アカウントの情報を取得
    const accountToDelete = accounts.find(account => account.id === id);
    if (accountToDelete) {
      // 確認ポップアップ用のデータを設定
      setConfirmationPopupData(accountToDelete);
      setShowConfirmationPopup(true);
    }
  };

  // 現在のユーザーかどうかを判定する関数
  const isCurrentUser = (accountId) => {
    return currentUser && currentUser.id === accountId;
  };

  // 確認ポップアップを閉じる関数
  const closeConfirmationPopup = () => {
    setShowConfirmationPopup(false);
  };

  // 削除を確定する関数
  const proceedToDelete = async () => {
    if (confirmationPopupData) {
      try {
        const token = localStorage.getItem('token');
        // ユーザー情報の is_active を false に変更
        await updateUser(confirmationPopupData.id, { is_active: false });
        
        // 削除処理が成功した後、ユーザーリストを再取得
        const response = await listUsers({
          skip: 0,
          limit: 100,
          is_admin: true,
          is_active: true
        });
        
        // 取得したユーザーリストで状態を更新
        setAccounts(response);
        
        // 削除成功のダイアログを表示
        showSuccessDialog(`${confirmationPopupData.username}のアカウントを削除しました。`);
        
        // ポップアップを閉じる
        closeConfirmationPopup();
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        // ポップアップを閉じる
        closeConfirmationPopup();
      }
    }
  };

  const handleCreate = () => {
    navigate('/admin/account/create');
  };

  const filteredAccounts = accounts.filter(account =>
    (account.username && account.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (account.accountname && account.accountname.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="header-container-admin">
      <AdminHeader onMenuToggle={(isOpen) => {
        // メニューの状態が変更された時の処理をここに追加できます
      }} />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="account-list-container">
            <div className="page-header">
              <div className="title-section">
                <div className="icon-square">
                  <img src={iconTitle} alt="icon" />
                </div>
                <h1>アカウント一覧</h1>
              </div>
            </div>
            <div className="search-container">
              <input
                className="search-input"
                type="text"
                placeholder="検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="loading">読み込み中...</div>
            ) : error ? (
              <div className="error">{error}</div>
            ) : (
              <div className="table-scroll-container">
                <table className="account-table">
                  <thead>
                    <tr>
                      <th>ユーザー名</th>
                      <th>アカウント名（メールアドレス）</th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccounts.length > 0 ? (
                      filteredAccounts.map(account => (
                        <tr key={account.id}>
                          <td>{account.username}</td>
                          <td>{account.accountname}</td>
                          <td>
                            <div className="button-container">
                              <button 
                                className={`view-button ${isCurrentUser(account.id) ? 'disabled' : ''}`}
                                onClick={() => handleDelete(account.id)}
                                disabled={isCurrentUser(account.id)}
                                title={isCurrentUser(account.id) ? '自分のアカウントは削除できません' : ''}
                              >
                                削除
                              </button>
                              <button className="edit-button" onClick={() => handleEdit(account.id)}>編集</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="no-data">データがありません</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <div className="button-group button-group--mt-l c-justify-center">
            <button type="button" className="button button-secondary button-medium" onClick={() => navigate(-1)}>
              戻る
            </button>
            <button type="submit" className="button button-primary button-medium" onClick={handleCreate}>
              新規作成
            </button>
          </div>
        </div>
      </div>

      {/* 削除確認ポップアップコンポーネント */}
      <ConfirmationPopup
        showConfirmationPopup={showConfirmationPopup}
        confirmationPopupData={confirmationPopupData}
        closeConfirmationPopup={closeConfirmationPopup}
        proceedToDetails={proceedToDelete}
      />
    </div>
  );
};

export default AccountList;